import { useQuery } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { 
  Package, 
  Cloud, 
  Clock, 
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Star,
  BookOpen,
  Target,
  Zap,
  Users,
  Calendar,
  DollarSign,
  BarChart3,
  Sparkles,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react'
import { useApi } from '../contexts/ApiContext'
import { formatDate, formatTimeLeft, getStockStatus, getStockStatusColor, getRarityColor, getRarityBadgeColor, formatRarity, getRaritySortValue } from '../lib/utils'
import { cn } from '../lib/utils'
import { ItemInfo } from '../types/api'
import { gameGuides, quickTips, weatherEffects, rarityValues } from '../lib/gameGuide'

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
}

const statVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { opacity: 1, scale: 1 }
}

export default function Dashboard() {
  const { isAuthenticated, fetchStock, fetchWeather, fetchCurrentEvent, fetchItemInfo } = useApi()

  const { data: stockData, isLoading: stockLoading } = useQuery({
    queryKey: ['stock'],
    queryFn: fetchStock,
    enabled: isAuthenticated,
    refetchInterval: 30000, // Refetch every 30 seconds
  })

  const { data: weatherData, isLoading: weatherLoading } = useQuery({
    queryKey: ['weather'],
    queryFn: fetchWeather,
    enabled: isAuthenticated,
    refetchInterval: 30000,
  })

  const { data: eventData, isLoading: eventLoading } = useQuery({
    queryKey: ['currentEvent'],
    queryFn: fetchCurrentEvent,
    enabled: isAuthenticated,
    refetchInterval: 60000, // Refetch every minute
  })

  const { data: itemInfoData } = useQuery({
    queryKey: ['itemInfo'],
    queryFn: () => fetchItemInfo(),
    enabled: isAuthenticated,
    staleTime: 300000, // Cache for 5 minutes
  })

  // Create a map of item_id to rarity for quick lookup
  const rarityMap = new Map<string, string>()
  if (itemInfoData) {
    itemInfoData.forEach((item: ItemInfo) => {
      if (item.item_id && item.rarity) {
        rarityMap.set(item.item_id, item.rarity)
      }
    })
  }

  if (!isAuthenticated) {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-center min-h-[60vh]"
      >
        <div className="text-center max-w-md space-modern">
          <div className="mb-6">
            <div className="mx-auto w-16 h-16 rounded-2xl bg-gradient-to-r from-primary to-primary/80 flex items-center justify-center mb-4">
              <Sparkles className="h-8 w-8 text-primary-foreground" />
            </div>
            <h2 className="text-2xl font-bold tracking-tight text-foreground">Welcome to Grow a Garden Tracker</h2>
          </div>
          <p className="text-muted-foreground leading-relaxed">
            Please configure your API key using environment variables to start tracking stock and weather data.
          </p>
          <div className="glass-card p-6 text-left mt-6">
            <p className="text-sm font-medium mb-3">Setup Instructions:</p>
            <ol className="text-xs text-muted-foreground space-y-2">
              <li>1. Create a <code className="bg-muted px-2 py-1 rounded-lg border border-border/30">.env</code> file in the project root</li>
              <li>2. Add <code className="bg-muted px-2 py-1 rounded-lg border border-border/30">VITE_API_KEY=your_api_key</code></li>
              <li>3. Restart the development server</li>
            </ol>
          </div>
        </div>
      </motion.div>
    )
  }

  const allStock = [
    ...(stockData?.seed_stock || []),
    ...(stockData?.gear_stock || []),
    ...(stockData?.egg_stock || []),
    ...(stockData?.cosmetic_stock || []),
    ...(stockData?.eventshop_stock || []),
  ]

  const activeWeather = weatherData?.weather?.filter(w => w.active) || []
  const lowStockItems = allStock.filter(item => getStockStatus(item.quantity) === 'low-stock')
  const outOfStockItems = allStock.filter(item => getStockStatus(item.quantity) === 'out-of-stock')
  
  // Get rare items using game-accurate rarity sorting
  const rareItems = allStock.filter(item => {
    const rarity = rarityMap.get(item.item_id) || 'common'
    const rarityValue = getRaritySortValue(rarity)
    // Consider items with rarity value <= 3 as rare (mythic, legendary, epic, rare)
    return rarityValue <= 3
  })

  // Calculate market insights
  const totalValue = allStock.reduce((sum, item) => {
    const rarity = rarityMap.get(item.item_id) || 'common'
    const baseValue = rarityValues[rarity as keyof typeof rarityValues]?.baseValue || 1
    return sum + (item.quantity * baseValue)
  }, 0)

  const stats = [
    {
      name: 'Total Items',
      value: allStock.length,
      icon: Package,
      color: 'from-info to-info/80',
      bgColor: 'bg-info/10',
      borderColor: 'border-info/30',
      description: 'Available in all shops',
      trend: '+12%',
      trendUp: true
    },
    {
      name: 'Active Weather',
      value: activeWeather.length,
      icon: Cloud,
      color: 'from-purple to-purple/80',
      bgColor: 'bg-purple/10',
      borderColor: 'border-purple/30',
      description: 'Current conditions',
      trend: '+2',
      trendUp: true
    },
    {
      name: 'Rare Items',
      value: rareItems.length,
      icon: Star,
      color: 'from-orange to-orange/80',
      bgColor: 'bg-orange/10',
      borderColor: 'border-orange/30',
      description: 'Rare and above',
      trend: '+5%',
      trendUp: true
    },
    {
      name: 'Market Value',
      value: `$${totalValue.toLocaleString()}`,
      icon: DollarSign,
      color: 'from-success to-success/80',
      bgColor: 'bg-success/10',
      borderColor: 'border-success/30',
      description: 'Total estimated value',
      trend: '+8%',
      trendUp: true
    },
  ]

  const currentTip = quickTips[Math.floor(Math.random() * quickTips.length)]

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-modern"
    >
      <div className="space-modern-sm">
        <div className="flex items-center space-x-3 mb-4">
          <div className="p-3 rounded-2xl bg-gradient-to-r from-primary to-primary/80 shadow-lg">
            <Sparkles className="h-6 w-6 text-primary-foreground" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-foreground">
              Dashboard
            </h1>
            <p className="text-muted-foreground leading-relaxed">
              Real-time overview of Grow a Garden stock, weather, and market insights
            </p>
          </div>
        </div>
      </div>

      {/* Enhanced Quick Tip */}
      <motion.div variants={itemVariants} className="gaming-card accent-blue">
        <div className="card-content mt-5">
          <div className="flex items-start space-x-4">
            <div className="p-3 rounded-xl bg-gradient-to-r from-info/20 to-info/10 border border-info/30">
              <Zap className="h-6 w-6 text-info" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-info mb-2">Pro Tip</h3>
              <p className="text-info/90 text-sm leading-relaxed">{currentTip}</p>
            </div>
            <div className="p-2 rounded-lg bg-info/10">
              <ArrowUpRight className="h-4 w-4 text-info" />
            </div>
          </div>
        </div>
      </motion.div>

      {/* Enhanced Stats Grid */}
      <div className="grid-modern md:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.name}
            variants={statVariants}
            className="gaming-card hover-lift group"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={cn("p-3 rounded-xl", stat.bgColor, stat.borderColor, "border")}>
                <stat.icon className={cn("h-6 w-6", stat.color.replace('from-', 'text-').replace(' to-', ''))} />
              </div>
              <div className="flex items-center space-x-1">
                {stat.trendUp ? (
                  <ArrowUpRight className="h-4 w-4 text-success" />
                ) : (
                  <ArrowDownRight className="h-4 w-4 text-destructive" />
                )}
                <span className={cn("text-xs font-medium", stat.trendUp ? "text-success" : "text-destructive")}>
                  {stat.trend}
                </span>
              </div>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">
                {stat.name}
              </p>
              <p className="text-2xl font-bold tracking-tight text-foreground">
                {stockLoading ? (
                  <div className="h-8 w-20 bg-muted/50 rounded-lg shimmer"></div>
                ) : (
                  stat.value
                )}
              </p>
              <p className="text-xs text-muted-foreground">
                {stat.description}
              </p>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="grid-modern lg:grid-cols-3">
        {/* Enhanced Current Weather with Effects */}
        <motion.div variants={itemVariants} className="gaming-card lg:col-span-2">
          <div className="card-header">
            <div className="flex items-center space-x-3">
              <div className="p-3 rounded-xl bg-gradient-to-r from-purple/20 to-purple/10 border border-purple/30">
                <Cloud className="h-6 w-6 text-purple" />
              </div>
              <h3 className="card-title">Current Weather & Effects</h3>
            </div>
          </div>
          <div className="card-content">
            {weatherLoading ? (
              <div className="flex items-center justify-center h-32">
                <div className="loading-spinner h-8 w-8"></div>
              </div>
            ) : activeWeather.length > 0 ? (
              <div className="space-modern-sm">
                {activeWeather.map((weather, index) => {
                  const weatherType = weather.weather_name.toLowerCase()
                  const effects = weatherEffects[weatherType as keyof typeof weatherEffects]
                  
                  return (
                    <motion.div
                      key={weather.weather_id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-center justify-between p-4 glass rounded-xl border border-border/30 transition-modern hover:bg-muted/20 hover:scale-[1.02]"
                    >
                      <div className="flex items-center space-x-4">
                        {weather.icon && (
                          <div className="p-2 rounded-xl bg-muted/50 border border-border/30">
                            <img 
                              src={weather.icon} 
                              alt={weather.weather_name} 
                              className="w-8 h-8"
                            />
                          </div>
                        )}
                        <div>
                          <p className="font-semibold text-foreground">{weather.weather_name}</p>
                          <p className="text-sm text-muted-foreground">
                            {effects?.description || 'Weather condition active'}
                          </p>
                          {effects && (
                            <div className="flex items-center space-x-4 mt-2 text-xs">
                              <span className="text-success font-medium bg-success/10 px-2 py-1 rounded-lg">
                                Growth: +{Math.round((effects.growth - 1) * 100)}%
                              </span>
                              <span className="text-info bg-info/10 px-2 py-1 rounded-lg">
                                Yield: +{Math.round((effects.yield - 1) * 100)}%
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-foreground">
                          {formatTimeLeft(weather.end_duration_unix)}
                        </p>
                        <p className="text-xs text-muted-foreground">remaining</p>
                      </div>
                    </motion.div>
                  )
                })}
              </div>
            ) : (
              <div className="flex items-center justify-center h-32 text-muted-foreground">
                <div className="text-center space-y-3">
                  <div className="mx-auto w-12 h-12 rounded-xl bg-muted/50 border border-border/30 flex items-center justify-center">
                    <Cloud className="h-6 w-6 opacity-50" />
                  </div>
                  <p>Clear skies - normal growing conditions</p>
                </div>
              </div>
            )}
          </div>
        </motion.div>

        {/* Enhanced Current Event */}
        <motion.div variants={itemVariants} className="gaming-card">
          <div className="card-header">
            <div className="flex items-center space-x-3">
              <div className="p-3 rounded-xl bg-gradient-to-r from-orange/20 to-orange/10 border border-orange/30">
                <Calendar className="h-6 w-6 text-orange" />
              </div>
              <h3 className="card-title">Current Event</h3>
            </div>
          </div>
          <div className="card-content">
            {eventLoading ? (
              <div className="flex items-center justify-center h-32">
                <div className="loading-spinner h-8 w-8"></div>
              </div>
            ) : eventData?.current ? (
              <div className="space-modern-sm">
                <div className="flex items-center space-x-4 p-4 glass rounded-xl border border-border/30">
                  {eventData.current.icon && (
                    <div className="p-2 rounded-xl bg-muted/50 border border-border/30">
                      <img src={eventData.current.icon} alt={eventData.current.name} className="w-8 h-8" />
                    </div>
                  )}
                  <div>
                                            <p className="font-semibold text-foreground">{eventData.current.name}</p>
                    <p className="text-sm text-muted-foreground">
                      Starts at {eventData.current.start.hour.toString().padStart(2, '0')}:
                      {eventData.current.start.minute.toString().padStart(2, '0')}
                    </p>
                  </div>
                </div>
                {eventData.current.description && (
                  <p className="text-sm text-muted-foreground p-3 glass rounded-xl border border-border/30">
                    {eventData.current.description}
                  </p>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center h-32 text-muted-foreground">
                <div className="text-center space-y-3">
                  <div className="mx-auto w-12 h-12 rounded-xl bg-muted/50 border border-border/30 flex items-center justify-center">
                    <Calendar className="h-6 w-6 opacity-50" />
                  </div>
                  <p>No active event</p>
                </div>
              </div>
            )}
          </div>
        </motion.div>

        {/* Enhanced Market Insights */}
        <motion.div variants={itemVariants} className="gaming-card">
          <div className="card-header">
            <div className="flex items-center space-x-3">
              <div className="p-3 rounded-xl bg-gradient-to-r from-success/20 to-success/10 border border-success/30">
                <BarChart3 className="h-6 w-6 text-success" />
              </div>
              <h3 className="card-title">Market Insights</h3>
            </div>
          </div>
          <div className="card-content space-modern-sm">
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 glass rounded-xl border border-border/30 hover:bg-muted/20 transition-modern">
                <div className="flex items-center space-x-3">
                  <div className="p-2 rounded-lg bg-info/10 border border-info/30">
                    <Package className="h-4 w-4 text-info" />
                  </div>
                  <span className="text-sm font-medium">Total Items</span>
                </div>
                <span className="font-bold text-lg">{allStock.length}</span>
              </div>
              
              <div className="flex items-center justify-between p-3 glass rounded-xl border border-border/30 hover:bg-muted/20 transition-modern">
                <div className="flex items-center space-x-3">
                  <div className="p-2 rounded-lg bg-orange/10 border border-orange/30">
                    <Star className="h-4 w-4 text-orange" />
                  </div>
                  <span className="text-sm font-medium">Rare Items</span>
                </div>
                <span className="font-bold text-lg">{rareItems.length}</span>
              </div>
              
              <div className="flex items-center justify-between p-3 glass rounded-xl border border-border/30 hover:bg-muted/20 transition-modern">
                <div className="flex items-center space-x-3">
                  <div className="p-2 rounded-lg bg-success/10 border border-success/30">
                    <DollarSign className="h-4 w-4 text-success" />
                  </div>
                  <span className="text-sm font-medium">Avg Value</span>
                </div>
                <span className="font-bold text-lg">
                  ${allStock.length > 0 ? Math.round(totalValue / allStock.length) : 0}
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Enhanced Low Stock Alerts */}
        <motion.div variants={itemVariants} className="gaming-card">
          <div className="card-header">
            <div className="flex items-center space-x-3">
              <div className="p-3 rounded-xl bg-gradient-to-r from-destructive/20 to-destructive/10 border border-destructive/30">
                <AlertTriangle className="h-6 w-6 text-destructive" />
              </div>
              <h3 className="card-title">Low Stock Alerts</h3>
            </div>
          </div>
          <div className="card-content">
            {stockLoading ? (
              <div className="flex items-center justify-center h-32">
                <div className="loading-spinner h-8 w-8"></div>
              </div>
            ) : lowStockItems.length > 0 ? (
              <div className="space-y-2">
                {lowStockItems.slice(0, 5).map((item, index) => {
                  const rarity = rarityMap.get(item.item_id) || 'unknown'
                  return (
                    <motion.div
                      key={item.item_id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-center justify-between p-3 glass rounded-xl border border-border/30 transition-modern hover:bg-muted/20 hover:scale-[1.02]"
                    >
                      <div className="flex items-center space-x-3">
                        {item.icon && (
                          <div className="p-1 rounded-lg bg-muted/50 border border-border/30">
                            <img src={item.icon} alt={item.display_name} className="w-5 h-5" />
                          </div>
                        )}
                        <span className="text-sm font-medium">{item.display_name}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-semibold px-2 py-1 rounded-lg bg-muted/50 border border-border/30">
                          {item.quantity}
                        </span>
                        <div className="flex items-center space-x-1">
                          <Star className={cn("h-3 w-3", getRarityColor(rarity))} />
                          <span className={cn(
                            "text-xs font-medium px-2 py-1 rounded-lg border border-border/30",
                            getRarityBadgeColor(rarity)
                          )}>
                            {formatRarity(rarity)}
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  )
                })}
                {lowStockItems.length > 5 && (
                  <p className="text-sm text-muted-foreground text-center pt-2">
                    +{lowStockItems.length - 5} more items
                  </p>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center h-32 text-muted-foreground">
                <div className="text-center space-y-3">
                  <div className="mx-auto w-12 h-12 rounded-xl bg-success/10 border border-success/30 flex items-center justify-center">
                    <CheckCircle className="h-6 w-6 text-success" />
                  </div>
                  <p>All items well stocked</p>
                </div>
              </div>
            )}
          </div>
        </motion.div>

        {/* Enhanced Game Guide Preview */}
        <motion.div variants={itemVariants} className="gaming-card">
          <div className="card-header">
            <div className="flex items-center space-x-3">
              <div className="p-3 rounded-xl bg-gradient-to-r from-pink/20 to-pink/10 border border-pink/30">
                <BookOpen className="h-6 w-6 text-pink" />
              </div>
              <h3 className="card-title">Game Guide</h3>
            </div>
          </div>
          <div className="card-content">
            <div className="space-modern-sm">
              {gameGuides.slice(0, 3).map((guide, index) => (
                <motion.div
                  key={index}
                  whileHover={{ scale: 1.02 }}
                  className="p-3 glass rounded-xl border border-border/30 transition-modern hover:bg-muted/20 cursor-pointer"
                >
                  <h4 className="font-semibold text-sm">{guide.title}</h4>
                  <p className="text-xs text-muted-foreground mt-1">
                    {guide.category.charAt(0).toUpperCase() + guide.category.slice(1)} • {guide.tags.slice(0, 2).join(', ')}
                  </p>
                </motion.div>
              ))}
              <div className="text-center pt-2">
                <button className="text-sm text-muted-foreground hover:text-foreground transition-modern font-medium">
                  View all guides →
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Enhanced Recent Updates */}
      <motion.div variants={itemVariants} className="gaming-card">
        <div className="card-header">
          <div className="flex items-center space-x-3">
            <div className="p-3 rounded-xl bg-gradient-to-r from-info/20 to-info/10 border border-info/30">
              <TrendingUp className="h-6 w-6 text-info" />
            </div>
            <h3 className="card-title">Recent Updates</h3>
          </div>
        </div>
        <div className="card-content">
          {stockLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="loading-spinner h-8 w-8"></div>
            </div>
          ) : stockData?.notification && stockData.notification.length > 0 ? (
            <div className="space-y-2">
              {stockData.notification.slice(0, 3).map((notification, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="p-3 glass rounded-xl border border-border/30 transition-modern hover:bg-muted/20 hover:scale-[1.01]"
                >
                  <p className="text-sm font-medium">{notification.message}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatDate(notification.timestamp * 1000)}
                  </p>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-32 text-muted-foreground">
              <div className="text-center space-y-3">
                <div className="mx-auto w-12 h-12 rounded-xl bg-muted/50 border border-border/30 flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 opacity-50" />
                </div>
                <p>No recent updates</p>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  )
} 